/* eslint-disable @typescript-eslint/ban-ts-comment */
import { DEFAULT_LAUNCH_SCREEN } from '@/settings';
import { IActionCategory, IAreaTypes, IComponentType, IImageTrackingOrientation, initCnDoc, ITrackingTypes, ITransitionEffectTypes, IUnitTypes, ROOT_COMPNENT_ID, } from '@r3f-component-data-structure';
import { ITargetImageReferenceObject } from '@r3f-component-data-structure/types/targetImageReferenceObject';
import { isAbstractComponent, isAbstractComponentType } from '@r3f/utils/general';
import * as Sentry from '@sentry/react';
import { MathUtils } from 'three';
import { cdsClient } from '../../sync/cds';
import { adjustRenderOrdersOnEntityRemoval, getHighestActiveRenderOrder, getParentSceneId, isSceneContainsSpatialArComponent } from '../../utils/general';
import { getComponentParentIdById } from '../../utils/pure';
import { copyAbstractComponent, copySpatialComponent } from '../../utils/pure/copy';
import { createFaceLandmarkGroup, createScreenAnchorGroup } from '../../utils/pure/create';
import { generateOldToNewIdDict } from '../../utils/pure/generateOldToNewIdDict';
import { getEntityDropTitle, getParentIdForSpatialComponentOnPaste, getSceneTitles, getScreenContentIdBySceneId } from '../../utils/pure/get';
import { insertAbstractComponent } from '../../utils/pure/insertAbstractComponent';
import { insertParentForSpatialComponentIfRequired } from '../../utils/pure/insertParentForSpatialComponentIfRequired';
import { insertSpatialComponent } from '../../utils/pure/insertSpatialComponent';
import { IContentDocActionTypes } from '../actions/action-types';
const initialState = {
    contentDoc: initCnDoc(),
};
// conditionally update docs
const updateContentDocFromBackend = (state, action) => {
    return {
        ...state,
        contentDoc: action.contentDoc,
    };
};
const addEntityContentDoc = (state, action) => {
    const { parentId, entity } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.componentsById[parentId].children.push(entity.id);
        doc.componentsById[entity.id] = entity;
    });
    return {
        ...state,
        contentDoc,
    };
};
const setPositionsInContentDoc = (state, action) => {
    const positionDict = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        for (const id in positionDict) {
            doc.componentsById[id].position = positionDict[id];
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
// const setRgbaColorsInContentDoc = (state: IContentReducer, action: IOnSetRgbaColors_Cn_Doc_Action): IContentReducer => {
// 	const colorDict = action.payload;
// 	const contentDoc = cdsClient.changeContentDoc((doc) => {
// 		for (const id in colorDict) {
// 			if (doc.componentsById[id].type !== IComponentType.Button) continue;
// 			(doc.componentsById[id] as IButton).color = colorDict[id];
// 		}
// 	});
// 	return {
// 		...state,
// 		contentDoc,
// 	};
// };
const setRotationsInContentDoc = (state, action) => {
    const rotationDict = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        for (const id in rotationDict) {
            doc.componentsById[id].rotation = rotationDict[id];
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const setScalesInContentDoc = (state, action) => {
    const scaleDict = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        for (const id in scaleDict) {
            const component = doc.componentsById[id];
            doc.componentsById[id].scale = scaleDict[id];
            if ('scalesInverted' in component) {
                component.scalesInverted = [scaleDict[id][0] < 0, scaleDict[id][1] < 0];
            }
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const setActiveSceneChildren = (state, action) => {
    const { activeSceneId, sceneChildren } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const scene = doc.componentsById[activeSceneId];
        if (scene.type === IComponentType.Scene) {
            scene.children = sceneChildren;
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onSetRootChildren = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const rootComponent = doc.componentsById[doc.rootComponentId];
        rootComponent.children = action.payload;
    });
    return {
        ...state,
        contentDoc,
    };
};
const setImageTracking_Cn_Doc = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (!doc.tracking)
            doc.tracking = {};
        const { id } = action.payload;
        let currentImageTrackingData = (doc.tracking[ITrackingTypes.image]?.[id] ? JSON.parse(JSON.stringify(doc.tracking[ITrackingTypes.image]?.[id])) : {});
        // Set defaults if nothing there
        if (!currentImageTrackingData) {
            currentImageTrackingData = {
                orientation: IImageTrackingOrientation.upright,
                units: IUnitTypes.mm,
                aspectRatio: 1 / 1.41,
                opacity: 1,
                scale: [1, 1, 1],
                targetAreaData: {
                    heightInMm: 297,
                    targetArea: IAreaTypes.a4,
                },
            };
        }
        if (!doc.tracking[ITrackingTypes.image])
            doc.tracking[ITrackingTypes.image] = {};
        doc.tracking[ITrackingTypes.image][id] = { ...currentImageTrackingData, ...action.payload };
    });
    return { ...state, contentDoc };
};
const removeComponentsFromContentDoc = (state, action) => {
    const { ids: componentIds } = action.payload;
    if (!componentIds.length)
        return state;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        for (let i = 0; i < componentIds.length; i++) {
            const componentId = componentIds[i];
            // Remove actions that are linked to deleted entity via target ids
            for (const id in doc.componentsById) {
                const component = doc.componentsById[id];
                if (!component.actions)
                    continue;
                const componentActions = component.actions;
                for (const triggerType in component.actions) {
                    const actionArray = componentActions[triggerType];
                    if (!actionArray)
                        continue;
                    for (let i = 0; i < actionArray.length; i++) {
                        const action = actionArray[i];
                        const { targetIds } = action;
                        if (!targetIds)
                            continue;
                        if (targetIds.includes(componentId)) {
                            delete actionArray[i];
                        }
                    }
                }
            }
        }
        // Remove deleted component from document
        for (let i = 0; i < componentIds.length; i++) {
            const componentId = componentIds[i];
            // adjust render order for other entities
            if (doc.componentsById[componentId])
                adjustRenderOrdersOnEntityRemoval(doc.componentsById, componentId);
            // remove component
            if (doc.componentsById[componentId])
                delete doc.componentsById[componentId];
            // remove component id from children of other components
            for (const id in doc.componentsById) {
                const { children } = doc.componentsById[id];
                if (!children)
                    continue;
                doc.componentsById[id].children = children.filter((id) => componentId !== id);
            }
        }
        // // Remove any media file entries that have become unreferenced from the media file dictionary
        // removeUnreferencedMediaFileReferences(doc);
    });
    return {
        ...state,
        contentDoc,
    };
};
const setComponentTransition_Cn_Doc = (state, action) => {
    const { ids, category, type, duration, delay } = action.payload;
    if (!ids)
        return state;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        for (let i = 0; i < ids.length; i++) {
            if (doc.componentsById[ids[i]].type === IComponentType.Root)
                continue;
            const component = doc.componentsById[ids[i]];
            // to keep content doc lean delete transition data if noEffect selected
            if (type === ITransitionEffectTypes.noEffect) {
                // if no transition data doc no need to delete data
                if (typeof component.transitions !== 'object')
                    continue;
                const transDataEmpty = Object.keys(component.transitions).length === 0;
                // if data for trans category exists delete
                if (!transDataEmpty)
                    delete component.transitions[category];
                // else delete whole transition object
                else {
                    delete component.transitions;
                }
            }
            else {
                if (!component.transitions)
                    component.transitions = {};
                component.transitions[category] = { type, duration, delay };
            }
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const replaceImage = (entity, replacementEntity) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        // Retain current title if the user has changed it
        // If currentTitle = someName.png or someName.jpeg then assume not changed
        const currentEntity = doc.componentsById[entity.id];
        const currentTitle = currentEntity.title;
        if (currentTitle.split('')[currentTitle.length - 4] !== '.' && currentTitle.split('')[currentTitle.length - 5] !== '.') {
            replacementEntity.title = currentEntity.title;
        }
        // Adjust the largest dimension to fit that of the respective current entity dimension
        const [x, y] = replacementEntity.scale;
        const aspectRatio = x / y;
        if (x > y) {
            replacementEntity.scale[0] = currentEntity.scale[0];
            replacementEntity.scale[1] = replacementEntity.scale[0] / aspectRatio;
        }
        else if (y > x) {
            replacementEntity.scale[1] = currentEntity.scale[1];
            replacementEntity.scale[0] = replacementEntity.scale[1] * aspectRatio;
        }
        // Retain previous position, rotation, curvature
        replacementEntity.position = currentEntity.position;
        replacementEntity.rotation = currentEntity.rotation;
        replacementEntity.curvature = currentEntity.curvature;
        replacementEntity.isSnappedToTarget = currentEntity.isSnappedToTarget;
        doc.componentsById[entity.id] = {
            ...JSON.parse(JSON.stringify(currentEntity)),
            ...JSON.parse(JSON.stringify(replacementEntity)),
            id: currentEntity.id,
        };
    });
    return contentDoc;
};
const replaceModel3d = (entity, replacementEntity) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        // Retain current title if the user has changed it
        // If currentTitle = someName.glb or someName.gltf then assume not changed
        const currentEntity = doc.componentsById[entity.id];
        const currentTitle = currentEntity.title;
        if (currentTitle.split('')[currentTitle.length - 4] !== '.' && currentTitle.split('')[currentTitle.length - 5] !== '.') {
            replacementEntity.title = currentEntity.title;
        }
        // // Adjust the largest dimension to fit that of the respective current entity dimension
        const indexArray = [0, 1, 2];
        const largedDimensionIndex = replacementEntity.scale.indexOf(Math.max(...replacementEntity.scale));
        const adjFactor = currentEntity.scale[largedDimensionIndex] / replacementEntity.scale[largedDimensionIndex];
        indexArray.forEach((index) => {
            replacementEntity.scale[index] = replacementEntity.scale[index] * adjFactor;
        });
        // Retain previous position, rotation
        replacementEntity.position = currentEntity.position;
        replacementEntity.rotation = currentEntity.rotation;
        doc.componentsById[entity.id] = {
            ...JSON.parse(JSON.stringify(currentEntity)),
            ...JSON.parse(JSON.stringify(replacementEntity)),
            id: currentEntity.id,
        };
        // Remove linked animations if applicable
        for (let i = 0; i < Object.keys(doc.componentsById).length; i++) {
            const component = doc.componentsById[Object.keys(doc.componentsById)[i]];
            if (!isAbstractComponent(component)) {
                for (const triggerType in component.actions) {
                    const componentActions = component.actions[triggerType];
                    if (!componentActions)
                        continue;
                    for (let i = 0; i < componentActions.length; i++) {
                        if (!('targetIds' in componentActions[i]))
                            continue;
                        if (componentActions[i].targetIds.includes(entity.id) && componentActions[i].type === IActionCategory.animateModel) {
                            delete componentActions[i];
                        }
                    }
                }
            }
        }
        // Remove idle animation
        delete doc.componentsById[entity.id].idleAnimation;
    });
    return contentDoc;
};
const replaceVideo = (entity, replacementEntity) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        // Retain current title if the user has changed it
        // If currentTitle = someName.mp4 or someName.webm then assume not changed
        const currentEntity = doc.componentsById[entity.id];
        const currentTitle = currentEntity.title;
        if (currentTitle.split('')[currentTitle.length - 4] !== '.' && currentTitle.split('')[currentTitle.length - 5] !== '.') {
            replacementEntity.title = doc.componentsById[entity.id].title;
        }
        // Adjust the largest dimension to fit that of the respective current entity dimension
        const [x, y] = replacementEntity.scale;
        const aspectRatio = x / y;
        if (x > y) {
            replacementEntity.scale[0] = currentEntity.scale[0];
            replacementEntity.scale[1] = replacementEntity.scale[0] / aspectRatio;
        }
        else if (y > x) {
            replacementEntity.scale[1] = currentEntity.scale[1];
            replacementEntity.scale[0] = replacementEntity.scale[1] * aspectRatio;
        }
        // Retain previous position, rotation, curvature
        replacementEntity.position = currentEntity.position;
        replacementEntity.rotation = currentEntity.rotation;
        replacementEntity.curvature = currentEntity.curvature;
        replacementEntity.isSnappedToTarget = currentEntity.isSnappedToTarget;
        doc.componentsById[entity.id] = {
            ...JSON.parse(JSON.stringify(currentEntity)),
            ...JSON.parse(JSON.stringify(replacementEntity)),
            id: currentEntity.id,
        };
    });
    return contentDoc;
};
const replaceEntity = (state, action) => {
    const { entity, replacementEntity } = action.payload;
    const entityCp = JSON.parse(JSON.stringify(entity));
    let contentDoc;
    switch (entityCp.type) {
        case IComponentType.Image:
            contentDoc = replaceImage(entityCp, replacementEntity);
            break;
        case IComponentType.Model3d:
            contentDoc = replaceModel3d(entityCp, replacementEntity);
            break;
        case IComponentType.Video:
            contentDoc = replaceVideo(entityCp, replacementEntity);
            break;
        default:
            throw new Error('Entity type not supported');
    }
    return { ...state, contentDoc };
};
const copyScenesAtIndex_Cn_Doc = (state, action) => {
    const { sceneIds, adjustPositionToCamera } = action.payload;
    let { index } = action.payload;
    if (!sceneIds)
        return state;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        let componentsByIdCp = JSON.parse(JSON.stringify(doc.componentsById));
        const sceneTitles = getSceneTitles(JSON.parse(JSON.stringify(componentsByIdCp)));
        for (let i = 0; i < sceneIds.length; i++) {
            const componentToCopy = componentsByIdCp[sceneIds[i]];
            const trackingImageId = componentToCopy.trackingImageId;
            const newTitle = getEntityDropTitle(sceneTitles, componentToCopy.title, false);
            const sourceSceneIsAr = true;
            const isFlatOrientation = doc?.tracking?.[ITrackingTypes.image]?.[trackingImageId ?? '']?.orientation === IImageTrackingOrientation.flat;
            const dict = generateOldToNewIdDict({ componentToCopy, componentsById: componentsByIdCp, parentId: ROOT_COMPNENT_ID });
            if (!dict)
                continue;
            const copy = copyAbstractComponent({ abstractComponent: componentToCopy, oldToNewIdDict: dict, componentsById: componentsByIdCp, parentId: ROOT_COMPNENT_ID });
            if (!copy)
                continue;
            const componentsByIdOut = insertAbstractComponent({
                abstractComponent: copy,
                parentId: ROOT_COMPNENT_ID,
                sourceSceneIsAr,
                isFlatOrientation,
                newTitle,
                componentsById: componentsByIdCp,
                abstractComponentId: sceneIds[i],
                adjustPositionToCamera
            });
            componentsByIdCp = JSON.parse(JSON.stringify(componentsByIdOut));
            const newSceneId = dict[sceneIds[i]];
            if (newSceneId) {
                if (componentsByIdCp[ROOT_COMPNENT_ID].children && index > componentsByIdCp[ROOT_COMPNENT_ID].children.length)
                    index = componentsByIdCp[ROOT_COMPNENT_ID].children.length;
                // The new scene has already been inserted at the end - need to remove the end one and insert it at the correct location
                componentsByIdCp[ROOT_COMPNENT_ID].children?.splice(index, 0, newSceneId);
                componentsByIdCp[ROOT_COMPNENT_ID].children?.splice(componentsByIdCp[ROOT_COMPNENT_ID].children.length - 1, 1);
            }
        }
        doc.componentsById = JSON.parse(JSON.stringify(componentsByIdCp));
    });
    return {
        ...state,
        contentDoc,
    };
};
const copyScreenSceneContent = (state, action) => {
    const { activeSceneId, targetSceneId } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const componentsByIdCp = JSON.parse(JSON.stringify(doc.componentsById));
        const screenContentId = getScreenContentIdBySceneId(activeSceneId, componentsByIdCp);
        if (!screenContentId)
            return;
        const componentToCopy = componentsByIdCp[screenContentId];
        const dict = generateOldToNewIdDict({ componentToCopy, componentsById: componentsByIdCp, targetSceneId, parentId: activeSceneId });
        if (!dict)
            return;
        const copy = copyAbstractComponent({ abstractComponent: componentToCopy, oldToNewIdDict: dict, componentsById: componentsByIdCp, parentId: activeSceneId });
        if (!copy)
            return;
        const componentsByIdOut = insertAbstractComponent({
            abstractComponent: copy,
            abstractComponentId: screenContentId,
            parentId: targetSceneId,
            sourceSceneIsAr: true,
            isFlatOrientation: false,
            componentsById: componentsByIdCp,
            adjustPositionToCamera: false
        });
        if (!componentsByIdOut)
            return;
        doc.componentsById = JSON.parse(JSON.stringify(componentsByIdOut));
    });
    return {
        ...state,
        contentDoc,
    };
};
const pasteCopiedEntitiesInContentDoc = (state, action) => {
    const { activeSceneId, copiedIds, offset, newTitle, isScreenRelativeMode } = action.payload;
    if (!copiedIds || !activeSceneId)
        return state;
    const imageTrackingDataDict = state?.contentDoc?.tracking?.[ITrackingTypes.image];
    const isFlatOrientation = imageTrackingDataDict?.[activeSceneId]?.orientation === IImageTrackingOrientation.flat;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        let componentsByIdCp = JSON.parse(JSON.stringify(doc.componentsById));
        for (let i = 0; i < copiedIds.length; i++) {
            const componentToCopy = componentsByIdCp[copiedIds[i]];
            // Did the entity come from an AR scene?
            const sourceSceneId = getParentSceneId(copiedIds[i], componentsByIdCp);
            if (!sourceSceneId)
                continue;
            const sourceSceneIsAr = isSceneContainsSpatialArComponent(sourceSceneId, copiedIds[i], componentsByIdCp);
            // When pasting, we may need to create a group ( screen anchor or face landmark ) for the spatial component to be inserted 
            const sourceParentId = getComponentParentIdById(componentToCopy.id, componentsByIdCp);
            if (!sourceParentId)
                continue;
            const sourceParentComponent = componentsByIdCp[sourceParentId];
            const faceLandmark = sourceParentComponent.type === IComponentType.FaceLandmarkGroup ? sourceParentComponent.landmark : undefined;
            const screenAnchorGroupPosition = sourceParentComponent.type === IComponentType.ScreenAnchorGroup ? sourceParentComponent.anchorPositionType : undefined;
            const componentsByIdTemp = insertParentForSpatialComponentIfRequired({
                spatialComponent: componentToCopy,
                isScreenRelativeMode,
                isFlatOrientation,
                sourceSceneId,
                sourceSceneIsAr,
                componentsById: componentsByIdCp,
                targetSceneId: activeSceneId,
            });
            if (componentsByIdTemp)
                componentsByIdCp = JSON.parse(JSON.stringify(componentsByIdTemp));
            // After the above step, there will be a parentId for the spatial component, if there wasn't already
            const parentId = getParentIdForSpatialComponentOnPaste({ sceneId: activeSceneId, componentsById: componentsByIdCp, isScreenRelativeMode, faceLandmark, screenAnchorGroupPosition });
            if (!parentId)
                continue;
            const dict = generateOldToNewIdDict({ componentToCopy, componentsById: componentsByIdCp, targetSceneId: activeSceneId });
            if (!dict)
                continue;
            const copy = copySpatialComponent({ spatialComponent: componentToCopy, oldToNewIdDict: dict });
            if (!copy)
                continue;
            const newRenderOrder = getHighestActiveRenderOrder(componentsByIdCp, activeSceneId, isScreenRelativeMode) + 1;
            const componentsByIdOut = insertSpatialComponent({
                spatialComponent: copy,
                parentId,
                sourceSceneIsAr,
                sourceSceneId,
                isFlatOrientation,
                componentsById: componentsByIdCp,
                offset,
                newTitle,
                newRenderOrder,
                adjustPositionToCamera: !isScreenRelativeMode
            });
            if (!componentsByIdCp)
                continue;
            componentsByIdCp = JSON.parse(JSON.stringify(componentsByIdOut));
        }
        doc.componentsById = JSON.parse(JSON.stringify(componentsByIdCp));
    });
    return {
        ...state,
        contentDoc,
    };
};
const undoCnDoc = (state) => {
    if (!cdsClient.canUndo())
        return state;
    cdsClient.undo();
    return {
        ...state,
        contentDoc: cdsClient.getContentDoc(),
    };
};
const redoCnDoc = (state) => {
    if (!cdsClient.canRedo())
        return state;
    cdsClient.redo();
    return {
        ...state,
        contentDoc: cdsClient.getContentDoc(),
    };
};
const removeSceneReferences = (state, action) => {
    const { sceneIds } = action.payload;
    if (!sceneIds)
        return state;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        for (let i = 0; i < sceneIds.length; i++) {
            const sceneId = sceneIds[i];
            const allComponentIds = Object.keys(doc.componentsById);
            // loop through all components in content doc doc
            for (let i = 0; i < allComponentIds.length; i++) {
                const compId = allComponentIds[i];
                const comp = doc.componentsById[compId];
                // continue if not a spatial component or component has no actions to remove
                // otherwise delete action property
                if (isAbstractComponent(comp) || !comp.actions)
                    continue;
                for (const triggerType in comp.actions) {
                    const actionArray = comp.actions[triggerType];
                    if (!actionArray)
                        continue;
                    for (let i = 0; i < actionArray.length; i++) {
                        const action = actionArray[i];
                        if (action.type === IActionCategory.linkScene && action.sceneId === sceneId)
                            delete actionArray[i];
                    }
                }
            }
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onSetMultipleComponentProps_Cn_Doc = (state, action) => {
    const propArray = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (!doc.componentsById)
            doc.componentsById = {};
        for (let i = 0; i < propArray.length; i++) {
            const { id, ...rest } = propArray[i];
            const componentState = isAbstractComponentType(rest.type) ? rest : propArray[i];
            // console.log('REDUCER update fontsize', rest.fontSize)
            if (!doc.componentsById[id])
                doc.componentsById[id] = {};
            for (const k in componentState) {
                const key = k;
                const data = componentState[key];
                if (typeof data === 'object' && data !== null && !(data instanceof Array)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    doc.componentsById[id][key] = {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ...doc.componentsById[id][key],
                        ...data,
                    };
                    continue;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                doc.componentsById[id][key] = JSON.parse(JSON.stringify(data)); // Close the object to prevent Automerge: "Cannot create a reference to an existing document object"
            }
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onRemoveComponentAction_Cn_Doc = (state, action) => {
    const { entityId, triggerType, index } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const actions = doc.componentsById[entityId].actions;
        actions[triggerType].splice(index, 1);
        if (actions[triggerType]?.length === 0) {
            // if no actions associated with trigger remove trigger from data structure
            delete actions[triggerType];
            // remove empty action object if necessary
            if (Object.keys(actions).length === 0) {
                delete doc.componentsById[entityId].actions;
            }
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onReparentComponent = (state, action) => {
    const { id, oldParentId, newParentId } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const oldParent = doc.componentsById[oldParentId];
        const newParent = doc.componentsById[newParentId];
        oldParent.children = oldParent.children.filter((_id) => _id !== id);
        newParent.children.push(id);
    });
    return {
        ...state,
        contentDoc,
    };
};
const onAddFaceLandmarkToScene = (state, action) => {
    const { landmark, sceneId } = action.payload;
    const landmarkId = MathUtils.generateUUID();
    const faceLandmarkGroup = createFaceLandmarkGroup(landmark);
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.componentsById[landmarkId] = faceLandmarkGroup;
        doc.componentsById[sceneId].children.push(landmarkId);
    });
    return {
        ...state,
        contentDoc,
    };
};
const onAddAnchorGroupToScene = (state, action) => {
    const { anchorPositionType, sceneId } = action.payload;
    const newId = MathUtils.generateUUID();
    const anchorGroup = createScreenAnchorGroup(anchorPositionType);
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const sceneContentId = doc.componentsById[sceneId].children.filter((id) => doc.componentsById[id].type === IComponentType.ScreenContent)[0];
        doc.componentsById[newId] = anchorGroup;
        doc.componentsById[sceneContentId].children.push(newId);
    });
    return {
        ...state,
        contentDoc,
    };
};
const onAddComponentAction_Cn_Doc = (state, action) => {
    // Need entity, index, action
    const { entityId, actionData, triggerType } = action.payload;
    const actionDataStr = JSON.stringify(actionData);
    if (actionDataStr === '{}')
        Sentry.captureException(new Error('Empty action object'), {
            extra: { entityId, actionData, triggerType }
        });
    const actionDataCp = JSON.parse(actionDataStr);
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const entity = doc.componentsById[entityId];
        if (entity.actions && entity.actions[triggerType]) {
            entity.actions[triggerType]?.push(actionDataCp);
        }
        else {
            if (!entity.actions)
                entity.actions = {};
            entity.actions[triggerType] = [actionDataCp];
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onSetComponentAction_Cn_Doc = (state, action) => {
    const { entityId, index, triggerType, actionData } = action.payload;
    const actionDataCp = JSON.parse(JSON.stringify(actionData));
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const component = doc.componentsById[entityId];
        if (!component.actions)
            component.actions = {};
        if (!component.actions[triggerType])
            component.actions[triggerType] = [];
        component.actions[triggerType][index] = actionDataCp;
    });
    return {
        ...state,
        contentDoc,
    };
};
const removeImageTracking_Cn_Doc = (state, _action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        delete doc.tracking;
    });
    return {
        ...state,
        contentDoc,
    };
};
const setWebEmbed = (state, action) => {
    if (!action.payload)
        return state;
    const currentWebEmbed = state?.contentDoc?.webEmbed ? JSON.parse(JSON.stringify(state?.contentDoc?.webEmbed)) : {};
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.webEmbed = {
            ...currentWebEmbed,
            ...action.payload,
        };
    });
    return {
        ...state,
        contentDoc,
    };
};
const setAnalyticsTracking_Cn_Doc = (state, action) => {
    if (!action.payload)
        return state;
    const trackingDict = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.analyticsTracking = {
            ...trackingDict,
        };
    });
    return {
        ...state,
        contentDoc,
    };
};
const clearScreenSceneContent = (state, action) => {
    const sceneId = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const screenContentId = doc.componentsById[sceneId].children.filter((id) => doc.componentsById[id].type == IComponentType.ScreenContent)[0];
        const anchorGroupIds = doc.componentsById[screenContentId].children;
        for (let i = 0; i < anchorGroupIds.length; i++) {
            doc.componentsById[anchorGroupIds[i]].children = [];
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const removeIdlePoseTime = (state, action) => {
    const entityId = action.payload.entityId;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (doc.componentsById[entityId].idlePoseTime !== undefined) {
            delete doc.componentsById[entityId].idlePoseTime;
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const removeIdleAnimation = (state, action) => {
    const entityId = action.payload.entityId;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (doc.componentsById[entityId].idleAnimation !== undefined) {
            delete doc.componentsById[entityId].idleAnimation;
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const setProjectColor = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.projectColors = action.payload;
    });
    return {
        ...state,
        contentDoc,
    };
};
const onSetEmitterProperties = (state, action) => {
    const { entityId, settings } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const { emitter } = doc.componentsById[entityId];
        for (const k in settings) {
            const setting = settings[k];
            const key = k;
            if (typeof setting !== 'object' || setting === null) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                emitter[key] = setting;
                continue;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!emitter[key])
                emitter[key] = {};
            for (const valueKey in setting) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                emitter[key][valueKey] = setting[valueKey];
            }
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onSetEmitterGroupProperties = (state, action) => {
    const { entityId, settings } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const { emitterGroup } = doc.componentsById[entityId];
        for (const k in settings) {
            const setting = settings[k];
            const key = k;
            if (typeof setting !== 'object') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                emitterGroup[key] = setting;
                continue;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!emitterGroup[key])
                emitterGroup[key] = {};
            for (const valueKey in setting) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                emitterGroup[key][valueKey] = setting[valueKey];
            }
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onSetEmitterBaseProperties = (state, action) => {
    const { entityId, properties } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const emitter = doc.componentsById[entityId];
        for (const key in properties) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            emitter[key] = properties[key];
        }
    });
    return {
        ...state,
        contentDoc,
    };
};
const onRemoveTrackingImageData = (state, action) => {
    const trackerDataId = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const imageTrackingDict = doc.tracking?.[ITrackingTypes.image];
        if (!imageTrackingDict)
            return;
        delete imageTrackingDict[trackerDataId];
    });
    return {
        ...state,
        contentDoc
    };
};
const setTargetImageReferenceObject = (state, action) => {
    const { position, referenceObject } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const targetPos = position ?? state.contentDoc.targetImageReferenceObject?.position;
        if (typeof targetPos === 'undefined')
            throw new Error('Targe position not defined');
        doc.targetImageReferenceObject = {
            referenceObject: referenceObject || state.contentDoc.targetImageReferenceObject?.referenceObject || ITargetImageReferenceObject.none,
            position: targetPos
        };
    });
    return { ...state, contentDoc };
};
const setInitialTemplateSlug = (state, action) => {
    const initialTemplateSlug = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.initialTemplateSlug = initialTemplateSlug;
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetChromaKeyProperties = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (!doc.componentsById[action.payload.entityId].chromaKey) {
            doc.componentsById[action.payload.entityId].chromaKey = {};
            doc.componentsById[action.payload.entityId].chromaKey.color = [0, 1, 0];
            doc.componentsById[action.payload.entityId].chromaKey.smoothness = 0.08;
            doc.componentsById[action.payload.entityId].chromaKey.similarity = 0.05;
            doc.componentsById[action.payload.entityId].chromaKey.spill = 0.02;
        }
        if (typeof action.payload?.color != 'undefined')
            doc.componentsById[action.payload.entityId].chromaKey.color = action.payload.color == null ? null : [...action.payload.color];
        if (typeof action.payload?.smoothness != 'undefined')
            doc.componentsById[action.payload.entityId].chromaKey.smoothness = action.payload.smoothness;
        if (typeof action.payload?.similarity != 'undefined')
            doc.componentsById[action.payload.entityId].chromaKey.similarity = action.payload.similarity;
        if (typeof action.payload?.spill != 'undefined')
            doc.componentsById[action.payload.entityId].chromaKey.spill = action.payload.spill;
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetProjectShadows = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.shadowsEnabled = action.payload;
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetDevice = (state, action) => {
    console.log(cdsClient);
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (!!action.payload)
            doc.device = action.payload;
        else if (doc.device)
            delete doc.device;
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetActionTrigger = (state, action) => {
    const { componentId, oldTrigger, newTrigger, actionIndex } = action.payload;
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const component = doc.componentsById[componentId];
        const oldActionsCp = JSON.parse(JSON.stringify(component.actions?.[oldTrigger]));
        const slicedAction = oldActionsCp?.splice(actionIndex, 1);
        if (!component.actions[newTrigger]) {
            component.actions[newTrigger] = [];
        }
        component.actions[newTrigger].push(slicedAction[0]);
        if (oldActionsCp.length > 0) {
            component.actions[oldTrigger] = oldActionsCp;
        }
        else if (component.actions) {
            delete component.actions[oldTrigger];
        }
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetScene360Background = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const scene = doc.componentsById[action.payload.sceneId];
        if (scene.trackingType !== ITrackingTypes.content360)
            return state;
        const { filestoreId = scene.content360?.filestoreId, title = scene.content360?.title, muted, loop, mediaType } = action.payload;
        if (filestoreId === null) {
            scene.content360 = null;
            return;
        }
        const currentContent = JSON.parse(JSON.stringify(scene.content360 || {}));
        if (mediaType === 'image') {
            scene.content360 = {
                ...currentContent,
                filestoreId,
                type: 'image',
                title
            };
        }
        else {
            scene.content360 = {
                ...currentContent,
                filestoreId,
                type: 'video',
                title,
                loop: Boolean(loop),
                muted: Boolean(muted)
            };
        }
    });
    return { ...state, contentDoc };
};
const onSetScene360CameraRotation = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const { sceneId, rotation } = action.payload;
        const scene = doc.componentsById[sceneId];
        if (scene.trackingType !== ITrackingTypes.content360 || !scene.content360)
            return state;
        scene.content360.initialCameraRotation = rotation;
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetVideoRenditionProgress = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        const { id, ...rest } = action.payload;
        if (!doc.videoStatusDict)
            doc.videoStatusDict = {};
        if (Object.keys(rest).length === 0) {
            delete (doc.videoStatusDict[id]);
            return;
        }
        const progressUrl = 'progressUrl' in rest ? rest.progressUrl : undefined;
        const status = 'status' in rest ? rest.status : undefined;
        doc.videoStatusDict[id] = { progressUrl: progressUrl, status: status };
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetLauchScreenField = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (typeof doc.launchScreen === 'undefined') {
            doc.launchScreen = DEFAULT_LAUNCH_SCREEN;
        }
        doc.launchScreen.fields[action.payload.fieldId] = action.payload.field;
    });
    return {
        ...state,
        contentDoc
    };
};
const onSetLauchScreenId = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (typeof doc.launchScreen === 'undefined') {
            doc.launchScreen = DEFAULT_LAUNCH_SCREEN;
        }
        doc.launchScreen.id = action.payload.id;
        doc.launchScreen.title = action.payload.id;
    });
    return { ...state, contentDoc };
};
const onAddMediaFile = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (typeof doc?.mediaFilesById === 'undefined') {
            doc['mediaFilesById'] = {};
        }
        doc.mediaFilesById[action.payload.id] = action.payload;
    });
    return { ...state, contentDoc };
};
const onSetGrabnGoOptions = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        if (!doc.tracking)
            doc.tracking = {};
        if (!doc.tracking?.grabAndGo)
            doc.tracking.grabAndGo = {};
        doc.tracking.grabAndGo = {
            ...doc.tracking.grabAndGo,
            ...action.payload
        };
    });
    return { ...state, contentDoc };
};
const onSetIsWebxrEnabledExperience = (state, action) => {
    const contentDoc = cdsClient.changeContentDoc((doc) => {
        doc.isWebXREnabledExperience = action.payload;
    });
    return { ...state, contentDoc };
};
// REFACTOR No real solution for typing the action here, an Action union is considered an anti-pattern ( https://redux.js.org/usage/usage-with-typescript#avoid-action-type-unions ) and moving to Redux Toolkit is a much larger job
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const contentReducer = (state = initialState, action) => {
    switch (action.type) {
        case IContentDocActionTypes.SET_INITIAL_TEMPLATE_SLUG:
            return setInitialTemplateSlug(state, action);
        case IContentDocActionTypes.SET_TARGET_IMAGE_REFERENCE_OBJECT_CN_DOC:
            return setTargetImageReferenceObject(state, action);
        case IContentDocActionTypes.ADD_PROJECT_COLORS_CN_DOC:
            return setProjectColor(state, action);
        case IContentDocActionTypes.REMOVE_IDLE_POSE_TIME:
            return removeIdlePoseTime(state, action);
        case IContentDocActionTypes.REMOVE_IDLE_ANIMATION:
            return removeIdleAnimation(state, action);
        case IContentDocActionTypes.SET_ANALYTICS_TRACKING:
            return setAnalyticsTracking_Cn_Doc(state, action);
        case IContentDocActionTypes.CLEAR_SCREEN_SCENE_CONTENT:
            return clearScreenSceneContent(state, action);
        case IContentDocActionTypes.COPY_SCREEN_SCENE_CONTENT:
            return copyScreenSceneContent(state, action);
        case IContentDocActionTypes.SET_WEB_EMBED_CN_DOC:
            return setWebEmbed(state, action);
        case IContentDocActionTypes.REMOVE_IMAGE_TRACKING_CN_DOC:
            return removeImageTracking_Cn_Doc(state, action);
        case IContentDocActionTypes.UNDO_CN_DOC:
            return undoCnDoc(state);
        case IContentDocActionTypes.REDO_CN_DOC:
            return redoCnDoc(state);
        case IContentDocActionTypes.REPLACE_ENTITY_CN_DOC:
            return replaceEntity(state, action);
        case IContentDocActionTypes.ADD_ENTITY_CN_DOC:
            return addEntityContentDoc(state, action);
        case IContentDocActionTypes.REMOVE_ENTITIES_CN_DOC:
            return removeComponentsFromContentDoc(state, action);
        case IContentDocActionTypes.SET_ENTITY_POSITIONS_CN_DOC:
            return setPositionsInContentDoc(state, action);
        case IContentDocActionTypes.SET_ENTITY_ROTATIONS_CN_DOC:
            return setRotationsInContentDoc(state, action);
        case IContentDocActionTypes.SET_SCALE_FACTORS_CN_DOC:
            return setScalesInContentDoc(state, action);
        // case IContentDocActionTypes.SET_ENTITY_COLORS_CN_DOC:
        // 	return setRgbaColorsInContentDoc(state, action);
        case IContentDocActionTypes.CHANGE_CN_DOC_REDUX:
            return updateContentDocFromBackend(state, action);
        case IContentDocActionTypes.PASTE_COPIED_ENTITIES_CN_DOC:
            return pasteCopiedEntitiesInContentDoc(state, action);
        case IContentDocActionTypes.COPY_SCENES_AT_INDEX_CN_DOC:
            return copyScenesAtIndex_Cn_Doc(state, action);
        case IContentDocActionTypes.SET_COMPONENT_TRANSITION_CN_DOC:
            return setComponentTransition_Cn_Doc(state, action);
        case IContentDocActionTypes.SET_MULTIPLE_COMPONENT_PROPS_CN_DOC:
            return onSetMultipleComponentProps_Cn_Doc(state, action);
        case IContentDocActionTypes.REMOVE_SCENE_REFERENCES_CN_DOC:
            return removeSceneReferences(state, action);
        case IContentDocActionTypes.SET_IMAGE_TRACKING_CN_DOC:
            return setImageTracking_Cn_Doc(state, action);
        case IContentDocActionTypes.SET_ACTIVE_SCENE_CHILDREN:
            return setActiveSceneChildren(state, action);
        case IContentDocActionTypes.SET_ROOT_CHILDREN:
            return onSetRootChildren(state, action);
        case IContentDocActionTypes.ADD_COMPONENT_ACTION_CN_DOC:
            return onAddComponentAction_Cn_Doc(state, action);
        case IContentDocActionTypes.REMOVE_COMPONENT_ACTION_CN_DOC:
            return onRemoveComponentAction_Cn_Doc(state, action);
        case IContentDocActionTypes.SET_COMPONENT_ACTION_CN_DOC:
            return onSetComponentAction_Cn_Doc(state, action);
        case IContentDocActionTypes.REPARENT_COMPONENT_CN_DOC:
            return onReparentComponent(state, action);
        case IContentDocActionTypes.ADD_FACE_LANDMARK_TO_SCENE_CN_DOC:
            return onAddFaceLandmarkToScene(state, action);
        case IContentDocActionTypes.ADD_ANCHOR_GROUP_TO_SCENE_CN_DOC:
            return onAddAnchorGroupToScene(state, action);
        case IContentDocActionTypes.SET_EMITTER_PROPERTIES_CN_DOC:
            return onSetEmitterProperties(state, action);
        case IContentDocActionTypes.SET_EMITTER_GROUP_PROPERTIES_CN_DOC:
            return onSetEmitterGroupProperties(state, action);
        case IContentDocActionTypes.SET_EMITTER_BASE_PROPERTIES_CN_DOC:
            return onSetEmitterBaseProperties(state, action);
        case IContentDocActionTypes.REMOVE_TRACKING_IMAGE_DATA_CN_DOC:
            return onRemoveTrackingImageData(state, action);
        case IContentDocActionTypes.SET_CHROMA_KEY_PROPERTIES_CN_DOC:
            return onSetChromaKeyProperties(state, action);
        case IContentDocActionTypes.SET_PROJECT_SHADOWS_ENABLED_CN_DOC:
            return onSetProjectShadows(state, action);
        case IContentDocActionTypes.SET_DEVICE_CN_DOC:
            return onSetDevice(state, action);
        case IContentDocActionTypes.SET_ACTION_TRIGGER_CN_DOC:
            return onSetActionTrigger(state, action);
        case IContentDocActionTypes.SET_SCENE_360_BACKGROUND:
            return onSetScene360Background(state, action);
        case IContentDocActionTypes.SET_SCENE_360_INITIAL_CAMERA_ROTATION:
            return onSetScene360CameraRotation(state, action);
        case IContentDocActionTypes.SET_VIDEO_RENDITION_PROGRESS_DATA:
            return onSetVideoRenditionProgress(state, action);
        case IContentDocActionTypes.SET_LAUNCH_SCREEN_FIELD:
            return onSetLauchScreenField(state, action);
        case IContentDocActionTypes.SET_LAUNCH_SCREEN_ID:
            return onSetLauchScreenId(state, action);
        case IContentDocActionTypes.ADD_MEDIA_FILE:
            return onAddMediaFile(state, action);
        case IContentDocActionTypes.SET_GRAB_N_GO_OPTIONS:
            return onSetGrabnGoOptions(state, action);
        case IContentDocActionTypes.SET_IS_WEBXR_ENABLED_EXPERIENCE:
            return onSetIsWebxrEnabledExperience(state, action);
        default:
            return state;
    }
};
export default contentReducer;
